import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { ArrowLeft, CreditCard, Crown, Zap, Clock, Calendar, Trophy } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PaymentPage = ({ user, token, onBack, onPaymentSuccess }) => {
  const [pricingPackages, setPricingPackages] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    loadPricingPackages();
    checkPaymentReturn();
  }, []);

  const checkPaymentReturn = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (sessionId && token) {
      setIsLoading(true);
      pollPaymentStatus(sessionId);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 6;
    
    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.payment_status === 'paid') {
        setError("✅ Paiement confirmé ! Redirection en cours...");
        setTimeout(() => {
          onPaymentSuccess();
          window.history.replaceState({}, document.title, window.location.pathname);
        }, 2000);
        return;
      } else if (response.data.status === 'expired') {
        setError("⏰ Session de paiement expirée. Veuillez réessayer.");
        setIsLoading(false);
        return;
      }
      
      if (attempts < maxAttempts) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      } else {
        setError("⏳ Vérification du paiement en cours... Veuillez patienter.");
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
      setIsLoading(false);
      if (attempts >= maxAttempts) {
        setError("Erreur lors de la vérification du paiement. Contactez le support.");
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 3000);
      }
    }
  };

  const loadPricingPackages = async () => {
    try {
      // Try to get user-specific packages first, fallback to general packages
      const endpoint = user && token ? '/packages/user' : '/packages';
      const config = user && token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      
      const response = await axios.get(`${API}${endpoint}`, config);
      setPricingPackages(response.data.packages);
    } catch (error) {
      console.error("Error loading pricing packages:", error);
      // Fallback to general packages if user-specific fails
      try {
        const fallbackResponse = await axios.get(`${API}/packages`);
        setPricingPackages(fallbackResponse.data.packages);
      } catch (fallbackError) {
        setError("Erreur lors du chargement des forfaits");
      }
    }
  };

  const handlePayment = async (packageId) => {
    try {
      setError("");
      setIsLoading(true);
      
      const currentUrl = window.location.origin + window.location.pathname + "/payment";
      
      const response = await axios.post(`${API}/checkout`, {
        package_id: packageId,
        success_url: `${currentUrl}?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: currentUrl
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error("URL de paiement non reçue");
      }
    } catch (error) {
      console.error("Payment error:", error);
      setIsLoading(false);
      setError(error.response?.data?.detail || "Erreur lors de la création du paiement");
    }
  };

  const getPackageDetails = (id, pkg) => {
    // Generate details dynamically based on package ID and data
    const isWeekly = id.includes('weekly');
    const isMonthly = id.includes('monthly');
    const isDaily = id.includes('daily');
    const isCollege = id.includes('college');
    
    let icon = <Zap className="w-6 h-6" />;
    let benefits = ['Explications détaillées', 'Support LaTeX'];
    let popular = false;
    
    if (isDaily) {
      icon = <Clock className="w-6 h-6" />;
      benefits = ['Accès 24h', ...benefits];
    } else if (isWeekly) {
      icon = <Calendar className="w-6 h-6" />;
      benefits = ['Accès 7 jours', 'Économie 70%', ...benefits];
      popular = true; // Weekly is recommended
    } else if (isMonthly) {
      icon = <Trophy className="w-6 h-6" />;
      benefits = ['Accès 30 jours', 'Meilleur prix', 'Support prioritaire', ...benefits];
    }
    
    if (isCollege) {
      benefits = ['Niveau collège', 'Résolution simple'];
    } else {
      benefits = ['Tous niveaux', ...benefits];
    }
    
    return {
      icon,
      description: pkg.description || pkg.name,
      benefits,
      popular
    };
  };

  return (
    <div className="min-h-screen bg-black relative">
      {/* Matrix background */}
      <div className="matrix-bg">
        <div className="matrix-code"></div>
      </div>
      
      <div className="relative z-10">
        {/* Header */}
        <div className="border-b border-green-800/20 bg-black/80 backdrop-blur-md">
          <div className="container mx-auto px-4 py-6">
            <div className="flex items-center justify-between">
              <Button
                onClick={onBack}
                variant="outline"
                className="border-green-700 text-green-300 hover:bg-green-950"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
              
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-pink-400 to-pink-600 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-black" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-green-400">Crédits épuisés</h1>
                  <p className="text-green-300 text-sm">Choisissez votre formule</p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Badge variant="outline" className="border-green-500 text-green-400 bg-green-950/50">
                  {user?.free_credits || 0} crédits
                </Badge>
                {user?.is_premium && <Crown className="w-4 h-4 text-yellow-400" />}
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-12">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-green-400 mb-4">
              Continuez à résoudre vos problèmes mathématiques
            </h2>
            <p className="text-green-300 text-lg max-w-2xl mx-auto">
              Vous avez utilisé vos 3 crédits gratuits. Choisissez une formule pour accéder à des résolutions illimitées avec notre IA spécialisée GPT-5.
            </p>
          </div>

          {/* Error Display */}
          {error && (
            <Card className={`mb-8 ${
              error.includes('✅') ? 'bg-green-950/20 border-green-800/30' : 'bg-red-950/20 border-red-800/30'
            }`}>
              <CardContent className="py-4">
                <div className={`flex items-center justify-center space-x-2 ${
                  error.includes('✅') ? 'text-green-400' : 'text-red-400'
                }`}>
                  <span className="text-center">{error}</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {Object.entries(pricingPackages)
              .filter(([id]) => !id.includes('college_detailed'))
              .map(([id, pkg]) => {
                const details = getPackageDetails(id, pkg);
                return (
                  <Card key={id} className={`bg-black/40 border-green-800/30 backdrop-blur-md relative overflow-hidden transition-all hover:scale-105 ${
                    details.popular ? 'ring-2 ring-green-500' : ''
                  }`}>
                    {details.popular && (
                      <div className="absolute top-0 right-0 bg-green-600 text-black px-3 py-1 text-sm font-semibold">
                        Recommandé
                      </div>
                    )}
                    
                    <CardHeader className="text-center">
                      <div className="flex justify-center mb-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-green-600 to-emerald-600 rounded-full flex items-center justify-center text-black">
                          {details.icon}
                        </div>
                      </div>
                      <CardTitle className="text-green-400 text-2xl">{pkg.name}</CardTitle>
                      <p className="text-green-300 text-sm">{details.description}</p>
                    </CardHeader>
                    
                    <CardContent className="text-center">
                      <div className="mb-6">
                        <span className="text-5xl font-bold text-green-400">{pkg.price.toFixed(2)}€</span>
                        <p className="text-green-500 text-sm mt-1">
                          {id === 'daily_unlimited' && 'soit ~0.04€ par résolution'}
                          {id === 'weekly_unlimited' && 'soit ~0.008€ par résolution'}  
                          {id === 'monthly_unlimited' && 'soit ~0.003€ par résolution'}
                        </p>
                      </div>

                      <div className="space-y-2 mb-6">
                        {details.benefits.map((benefit, index) => (
                          <div key={index} className="flex items-center text-green-300 text-sm">
                            <div className="w-2 h-2 bg-green-400 rounded-full mr-3"></div>
                            {benefit}
                          </div>
                        ))}
                      </div>

                      <Button
                        onClick={() => handlePayment(id)}
                        disabled={isLoading}
                        className={`w-full h-12 font-semibold ${
                          details.popular 
                            ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 text-black' 
                            : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-black'
                        }`}
                      >
                        {isLoading ? (
                          <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin mr-2" />
                        ) : (
                          <CreditCard className="w-5 h-5 mr-2" />
                        )}
                        Choisir ce forfait
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
          </div>

          {/* College Options */}
          {Object.entries(pricingPackages).filter(([id]) => id.includes('college')).length > 0 && (
            <div className="max-w-2xl mx-auto">
              <h3 className="text-center text-blue-400 text-xl font-semibold mb-6">Options Ponctuelles</h3>
              <div className="grid gap-4">
                {Object.entries(pricingPackages)
                  .filter(([id]) => id.includes('college'))
                  .map(([id, pkg]) => (
                    <Card key={id} className="bg-blue-950/20 border-blue-800/30 backdrop-blur-md">
                      <CardContent className="py-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <h4 className="text-blue-300 font-medium">{pkg.name}</h4>
                            <p className="text-blue-400 text-sm">{pkg.description || 'Résolution détaillée'}</p>
                          </div>
                          <div className="flex items-center space-x-3">
                            <span className="text-blue-400 font-bold text-xl">
                              {pkg.price.toFixed(2)}€
                            </span>
                            <Button
                              onClick={() => handlePayment(id)}
                              disabled={isLoading}
                              size="sm"
                              variant="outline"
                              className="border-blue-600 text-blue-400 hover:bg-blue-950"
                            >
                              <CreditCard className="w-4 h-4 mr-1" />
                              Acheter
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
              </div>
            </div>
          )}

          {/* Security Info */}
          <div className="text-center mt-12">
            <p className="text-green-500 text-sm">
              🔒 Paiement 100% sécurisé par Stripe • Aucun engagement • Annulation possible
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};